/**
 * disclosuresApi — inline fetch wrapper for `/v3/admin/disclosures/catalog`.
 *
 * Tier-3 Disclosure Engine M4 scaffolding (per
 * `docs/port-design/tier-3-disclosure-engine-plan.md`). The Risk Mgmt portal
 * is the canonical authoring surface; this wrapper exposes the read-side
 * config envelope that already exists today (M1 deliverable).
 *
 * Backend route: GET /v3/admin/disclosures/catalog (composite envelope)
 *   {
 *     templates: DisclosureTemplate[],
 *     versions: DisclosureVersion[],
 *     sections: DisclosureSection[],
 *     variants: DisclosureVariant[],
 *     mergeFieldGroups: MergeFieldGroup[],
 *     mergeFieldDefinitions: MergeFieldDefinition[]
 *   }
 *
 * The shared `@retail-ops/api-client` types this route as
 * `ReadonlyArray<DisclosureTemplate>` (legacy stub) — until the api-client
 * gets the composite envelope wired (M3 task), the inline fetch + local
 * types are the safe path. Mirrors the riskReviewApi pattern.
 *
 * Auth: `Authorization: Bearer <msal>` (bearer-only — no function keys are
 * shipped in the browser bundle, per the owner security directive).
 */

import { getMsalToken } from '@/auth/msal';

// ---------------------------------------------------------------------------
// Types — the catalog route serializes the EF entities (camelCase), so these
// mirror libs/PCB.RetailOps.Data/Entities/Config/Disclosures/*.cs.
// ---------------------------------------------------------------------------

export interface DisclosureTemplateRow {
  code: string;
  displayName: string;
  description?: string | null;
  layer?: string | null;
  multiplicity?: string | null;
  defaultPageCount?: number | null;
  signatureRequirement?: string | null;
  defaultSignatureCount?: number | null;
  contentSource?: string | null;
  variantKey?: string | null;
  /** 'ALL' or a comma list of product types (Products.Category). */
  productCategory?: string | null;
  /** Comma list of product codes; empty = every product in the category. */
  applicableProducts?: string | null;
  /** 'ALL' or a comma list of customer types (EntityRules.CustomerType). */
  applicableEntityTypes?: string | null;
  audience?: string | null;
  activeVersionId?: number | null;
  sortOrder?: number | null;
  isInternal?: boolean;
  isActive?: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
  [k: string]: unknown;
}

/** dbo.DisclosureTemplateVersions — field names exactly as the catalog route serializes them. */
export interface DisclosureVersionRow {
  versionId: number;
  templateCode: string;
  versionNumber: string;
  /** Legacy alias of versionNumber (older screens read `.version`). Filled by the loader. */
  version?: string;
  status?: string | null;
  contentHtml?: string | null;
  fileUrl?: string | null;
  pageCount?: number | null;
  signatureCount?: number | null;
  variantSelector?: string | null;
  conditionExpression?: string | null;
  featureRequired?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  authoredBy?: string | null;
  authoredAt?: string | null;
  submittedBy?: string | null;
  submittedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  activatedBy?: string | null;
  activatedAt?: string | null;
  retiredBy?: string | null;
  retiredAt?: string | null;
  retirementReason?: string | null;
  rejectedBy?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  notes?: string | null;
  sourceCitationKey?: string | null;
  [k: string]: unknown;
}

export interface DisclosureSectionRow {
  code: string;
  title?: string | null;
  docCode?: string | null;
  category?: string | null;
  [k: string]: unknown;
}

export interface DisclosureVariantRow {
  variantKey: string;
  variantCode: string;
  customerType?: string | null;
  selectorExpr?: string | null;
  isActive?: boolean;
  [k: string]: unknown;
}

export interface MergeFieldGroupRow {
  code: string;
  name?: string | null;
  /** Legacy aliases (older screens). Filled by the loader. */
  groupCode?: string;
  displayName?: string | null;
  [k: string]: unknown;
}

export interface MergeFieldDefinitionRow {
  code: string;
  label?: string | null;
  groupCode?: string | null;
  formatter?: string | null;
  isRequired?: boolean;
  /** Legacy aliases (older screens). Filled by the loader. */
  fieldCode?: string;
  displayName?: string | null;
  [k: string]: unknown;
}

export interface DisclosureWorkflowLinkRow {
  workflowCode: string;
  stepCode: string;
  templateCode: string;
  isMandatory?: boolean;
  conditionExpression?: string | null;
  sortOrder?: number | null;
  isActive?: boolean;
  notes?: string | null;
  [k: string]: unknown;
}

export interface DisclosuresEnvelope {
  templates: ReadonlyArray<DisclosureTemplateRow>;
  versions: ReadonlyArray<DisclosureVersionRow>;
  sections: ReadonlyArray<DisclosureSectionRow>;
  variants: ReadonlyArray<DisclosureVariantRow>;
  mergeFieldGroups: ReadonlyArray<MergeFieldGroupRow>;
  mergeFieldDefinitions: ReadonlyArray<MergeFieldDefinitionRow>;
  workflowLinks: ReadonlyArray<DisclosureWorkflowLinkRow>;
}

const EMPTY_ENVELOPE: DisclosuresEnvelope = {
  templates: [],
  versions: [],
  sections: [],
  variants: [],
  mergeFieldGroups: [],
  mergeFieldDefinitions: [],
  workflowLinks: [],
};

// ---------------------------------------------------------------------------
// HTTP plumbing.
// ---------------------------------------------------------------------------

function getBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
    'https://pcb-retail-api.azurewebsites.net/api'
  );
}

async function buildHeaders(): Promise<Headers> {
  const headers = new Headers({
    Accept: 'application/json',
    'x-pcb-client-id': 'risk-mgmt-panel',
  });
  try {
    const token = await getMsalToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // Silent — RoleGate already enforced an authenticated session.
  }
  return headers;
}

function pickArray<T>(raw: unknown, key: string): ReadonlyArray<T> {
  if (!raw || typeof raw !== 'object') return [];
  const v = (raw as Record<string, unknown>)[key];
  return Array.isArray(v) ? (v as ReadonlyArray<T>) : [];
}

/**
 * Fetch the composite disclosures envelope. Returns an empty envelope on
 * error (best-effort — the read surface should never throw mid-render).
 */
export async function getDisclosuresConfig(
  signal?: AbortSignal,
): Promise<{ success: boolean; envelope: DisclosuresEnvelope; error?: string }> {
  const url = `${getBaseUrl()}/v3/admin/disclosures/catalog`;
  const headers = await buildHeaders();
  try {
    const resp = await fetch(url, {
      method: 'GET',
      headers,
      ...(signal ? { signal } : {}),
    });
    const text = await resp.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
    }
    if (!resp.ok) {
      const msg =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error?: unknown }).error)
          : `HTTP ${resp.status}`;
      return { success: false, envelope: EMPTY_ENVELOPE, error: msg };
    }
    // Backend envelope shape: ApiResult<{ templates, versions, ... }> OR raw
    // composite. Handle both since the route shape has wobbled.
    let payload: unknown = body;
    if (
      body &&
      typeof body === 'object' &&
      'data' in body &&
      (body as { data?: unknown }).data !== undefined
    ) {
      payload = (body as { data: unknown }).data;
    }
    // The server serializes entity property names (versionNumber, code, label, name).
    // Older screens read invented names (version, fieldCode, displayName, groupCode) and
    // showed "undefined" / blanks (Bill's 2026-10-06 capture). Fill the aliases once here.
    const envelope: DisclosuresEnvelope = {
      templates: pickArray<DisclosureTemplateRow>(payload, 'templates'),
      versions: pickArray<DisclosureVersionRow>(payload, 'versions').map((v) => ({
        ...v,
        version: String(v.versionNumber ?? ''),
      })),
      sections: pickArray<DisclosureSectionRow>(payload, 'sections'),
      variants: pickArray<DisclosureVariantRow>(payload, 'variants'),
      mergeFieldGroups: pickArray<MergeFieldGroupRow>(payload, 'mergeFieldGroups').map((g) => ({
        ...g,
        groupCode: String(g.code ?? ''),
        displayName: g.name ?? null,
      })),
      mergeFieldDefinitions: pickArray<MergeFieldDefinitionRow>(
        payload,
        'mergeFieldDefinitions',
      ).map((d) => ({ ...d, fieldCode: String(d.code ?? ''), displayName: d.label ?? null })),
      workflowLinks: pickArray<DisclosureWorkflowLinkRow>(payload, 'workflowLinks'),
    };
    return { success: true, envelope };
  } catch (err) {
    if (signal?.aborted) {
      return { success: false, envelope: EMPTY_ENVELOPE, error: 'aborted' };
    }
    const msg = err instanceof Error ? err.message : String(err);
    return { success: false, envelope: EMPTY_ENVELOPE, error: msg };
  }
}
